import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from "@angular/core";
import { EmotionProjectReply } from "~/app/textToSpeech/project.service";

@Component({
  selector: "app-emotion-reply",
  templateUrl: "./emotion-reply.component.html",
  styleUrls: ["./emotion-reply.component.scss"],
})
export class EmotionReplyComponent implements OnChanges {
  @Input() reply: EmotionProjectReply;
  @Input() index: number;
  @Input() editableDialogue = false;
  @Input() showEmotionLabels = false;
  @Input() isSaving = false;
  @Input() saveFailed = false;
  @Input() showSavedIndicator = false;
  @Input() playbackManagedExternally = false;
  @Input() externalIsPlaying = false;
  @Input() playEnabled = true;
  @Output() saveDialogue = new EventEmitter<string>();
  @Output() playRequested = new EventEmitter<void>();

  private currentAudio: HTMLAudioElement | null = null;
  private suppressNextBlurSave = false;
  public isPlaying = false;
  public dialogueDraft = "";

  public get playing(): boolean {
    return this.playbackManagedExternally
      ? this.externalIsPlaying
      : this.isPlaying;
  }

  public get formattedCount(): string {
    const displayIndex = Number(this.index) + 1;
    return displayIndex < 10 ? `0${displayIndex}` : `${displayIndex}`;
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.reply) {
      this.dialogueDraft = this.reply?.dialogue ?? "";
    }
  }

  public onDialogueInput(event: Event): void {
    const target = event.target as HTMLTextAreaElement;
    this.dialogueDraft = target?.value ?? "";
  }

  public get hasDialogueChanges(): boolean {
    return `${this.dialogueDraft}` !== `${this.reply?.dialogue ?? ""}`;
  }

  public get emotionLabels(): string[] {
    const raw = `${this.reply?.dominantEmotion ?? ""}`.trim();
    if (!raw) {
      return [];
    }

    return raw
      .split(/[,\|]/g)
      .map((label) => label.replace(/[[\]"']/g, "").trim())
      .filter((label) => !!label);
  }

  public getEmotionTagClass(label: string): string {
    const normalized = `${label || ""}`.trim().toLowerCase();

    if (
      ["happy", "joy", "excited", "love", "optimistic"].includes(normalized)
    ) {
      return "tag-deliver";
    }
    if (["sad", "grief", "melancholy"].includes(normalized)) {
      return "tag-share";
    }
    if (["angry", "anger", "frustrated", "furious"].includes(normalized)) {
      return "tag-publish";
    }
    if (["fear", "scared", "anxious", "nervous"].includes(normalized)) {
      return "tag-archive";
    }
    if (["surprised", "surprise"].includes(normalized)) {
      return "tag-find";
    }
    if (["disgust", "disgusted"].includes(normalized)) {
      return "tag-admin";
    }
    if (["neutral", "calm", "regular"].includes(normalized)) {
      return "tag-regular";
    }

    return "tag-find";
  }

  public saveDraft(): void {
    if (!this.hasDialogueChanges || this.isSaving) {
      return;
    }
    this.saveDialogue.emit(this.dialogueDraft);
  }

  public onDialogueBlur(): void {
    if (!this.editableDialogue) {
      return;
    }
    if (this.suppressNextBlurSave) {
      this.suppressNextBlurSave = false;
      return;
    }
    this.saveDraft();
  }

  public onDialogueKeydown(event: KeyboardEvent): void {
    if (!this.editableDialogue || event.key !== "Enter" || event.shiftKey) {
      return;
    }
    event.preventDefault();
    this.suppressNextBlurSave = true;
    this.saveDraft();
  }

  public togglePlay(): void {
    if (this.playbackManagedExternally) {
      if (!this.playEnabled) {
        return;
      }
      this.playRequested.emit();
      return;
    }

    if (!this.reply?.audioFilePath) {
      return;
    }

    if (this.isPlaying && this.currentAudio) {
      this.currentAudio.pause();
      this.currentAudio = null;
      this.isPlaying = false;
      return;
    }

    this.currentAudio = new Audio(this.reply.audioFilePath);
    this.isPlaying = true;
    this.currentAudio.addEventListener("ended", () => {
      this.currentAudio = null;
      this.isPlaying = false;
    });
    this.currentAudio.addEventListener("error", () => {
      this.currentAudio = null;
      this.isPlaying = false;
    });
    this.currentAudio.play().catch(() => {
      this.currentAudio = null;
      this.isPlaying = false;
    });
  }
}
